'use client';

// The fields of the "Register & identifiers" filter group, one component
// each. State stays in the sidebar; RegisterFilterGroup hands its props
// through.
import { swissCantonLabel } from '@/lib/countries';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover } from '@/components/ui/popover';
import {
  FacetCount,
  FIELD_LABEL_CLASS,
  FIELD_PLACEHOLDER_CLASS,
  FilterLabel,
  INPUT_PLACEHOLDER_CLASS,
} from '../FilterControls';
import { deFold, REGISTER_TYPE_OPTIONS } from '../searchSidebarOptions';
import {
  ComboboxContent,
  ComboboxTrigger,
  FilterChip,
  OptionCheck,
  submitOnEnter,
} from './FilterFieldParts';
import type { RegisterFilterGroupProps } from './RegisterFilterGroup';

/** euId — direct lookup. */
export function EuidField({
  t,
  staged,
  set,
  handleFormSubmit,
}: Pick<
  RegisterFilterGroupProps,
  't' | 'staged' | 'set' | 'handleFormSubmit'
>) {
  const { euid: currentEuid } = staged;
  return (
    <div className="space-y-1.5">
      <Label className={FIELD_LABEL_CLASS}>
        {t('search.sidebar.euid', {
          defaultValue: 'European Unique Identifier',
        })}
      </Label>
      <Input
        value={currentEuid}
        onChange={(e) => set.euid(e.target.value)}
        onKeyDown={submitOnEnter(handleFormSubmit)}
        placeholder={t('search.sidebar.euidPlaceholder')}
        className={`h-11 lg:h-9 text-base lg:text-sm font-mono ${INPUT_PLACEHOLDER_CLASS}`}
      />
    </div>
  );
}

/** Register ID — digits only. The HR* prefix lives in `registerType`; the
 *  numeric tail is what the backend matches against `register_id`.
 *  `inputMode="numeric"` brings up the phone keypad on mobile. */
export function RegisterIdField({
  t,
  staged,
  set,
  handleFormSubmit,
}: Pick<
  RegisterFilterGroupProps,
  't' | 'staged' | 'set' | 'handleFormSubmit'
>) {
  const { registerId: currentRegisterId } = staged;
  return (
    <div className="space-y-1.5">
      <Label className={FIELD_LABEL_CLASS}>
        {t('search.sidebar.registerId', { defaultValue: 'Register ID' })}
      </Label>
      <Input
        type="text"
        inputMode="numeric"
        pattern="\d*"
        value={currentRegisterId}
        onChange={(e) =>
          set.registerId(e.target.value.replace(/\D/g, ''))
        }
        onKeyDown={submitOnEnter(handleFormSubmit)}
        placeholder={t('search.sidebar.registerIdPlaceholder', {
          defaultValue: 'e.g. 100484',
        })}
        className={`h-11 lg:h-9 text-base lg:text-sm font-mono ${INPUT_PLACEHOLDER_CLASS}`}
      />
    </div>
  );
}

/** Register Court — a dropdown, not free text. The backend matches
 *  `register_court = $1` exactly, and the stored spellings are the
 *  register's own: "Berlin (Charlottenburg)", "Frankfurt am Main",
 *  "Oldenburg (Oldenburg)". As an input this silently returned zero hits for
 *  anything typed the obvious way. Options come from the MV via
 *  /api/search/filters/courts, so the list can't drift from what is actually
 *  searchable. */
export function RegisterCourtField({
  t,
  registerCourtOpen,
  setRegisterCourtOpen,
  setRegisterCourtSearch,
  staged,
  i18n,
  set,
  registerCourtSearch,
  courtList,
  facetCount,
}: Pick<
  RegisterFilterGroupProps,
  | 't'
  | 'registerCourtOpen'
  | 'setRegisterCourtOpen'
  | 'setRegisterCourtSearch'
  | 'staged'
  | 'i18n'
  | 'set'
  | 'registerCourtSearch'
  | 'courtList'
  | 'facetCount'
>) {
  const { registerCourt: currentRegisterCourt } = staged;
  return (
    <div className="space-y-1.5">
      <Label className={FIELD_LABEL_CLASS}>
        {t('search.sidebar.registerCourt', {
          defaultValue: 'Register Court',
        })}
      </Label>
      <Popover
        open={registerCourtOpen}
        onOpenChange={(open) => {
          setRegisterCourtOpen(open);
          if (!open) setRegisterCourtSearch('');
        }}
      >
        <ComboboxTrigger>
          {!currentRegisterCourt ? (
            <span className={FIELD_PLACEHOLDER_CLASS}>
              {t('search.sidebar.registerCourtPlaceholder', {
                defaultValue: 'e.g. Stuttgart',
              })}
            </span>
          ) : (
            <FilterChip onRemove={() => set.registerCourt('')}>
              <span className="max-w-[220px] truncate">
                {swissCantonLabel(currentRegisterCourt, i18n.language)}
              </span>
            </FilterChip>
          )}
        </ComboboxTrigger>
        <ComboboxContent>
          <Command shouldFilter={false}>
            <CommandInput
              className={INPUT_PLACEHOLDER_CLASS}
              placeholder={t('search.sidebar.searchCourts', {
                defaultValue: 'Search courts...',
              })}
              value={registerCourtSearch}
              onValueChange={setRegisterCourtSearch}
            />
            <CommandList className="max-h-[272px]">
              <CommandEmpty>
                {t('search.sidebar.noCourtFound', {
                  defaultValue: 'No court found.',
                })}
              </CommandEmpty>
              <CommandGroup>
                {/* de_fold so "Duren"/"Koln" find Düren/Köln, and
                    substring so "Charlottenburg" finds the Berlin
                    entry by its suffix. */}
                {courtList
                  .filter(
                    (c) =>
                      !registerCourtSearch ||
                      deFold(swissCantonLabel(c, i18n.language)).includes(
                        deFold(registerCourtSearch),
                      ) ||
                      deFold(c).includes(deFold(registerCourtSearch)),
                  )
                  .map((c) => (
                    <CommandItem
                      key={c}
                      value={c}
                      onSelect={() => {
                        set.registerCourt(
                          c === currentRegisterCourt ? '' : c,
                        );
                        setRegisterCourtOpen(false);
                        setRegisterCourtSearch('');
                      }}
                    >
                      <OptionCheck selected={currentRegisterCourt === c} />
                      <span className="truncate text-xs">
                        {swissCantonLabel(c, i18n.language)}
                      </span>
                      <FacetCount count={facetCount('register_court', c)} />
                    </CommandItem>
                  ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </ComboboxContent>
      </Popover>
    </div>
  );
}

/** Register Type — HRA / HRB / PR / VR / GnR / GsR. */
export function RegisterTypeField({
  t,
  registerTypeOpen,
  setRegisterTypeOpen,
  staged,
  set,
  registerTypeOptions,
  facetCount,
}: Pick<
  RegisterFilterGroupProps,
  | 't'
  | 'registerTypeOpen'
  | 'setRegisterTypeOpen'
  | 'staged'
  | 'set'
  | 'registerTypeOptions'
  | 'facetCount'
>) {
  const { registerType: currentRegisterType } = staged;
  return (
    <div className="space-y-1.5">
      <Label className={FIELD_LABEL_CLASS}>
        {t('search.sidebar.registerType', { defaultValue: 'Register Type' })}
      </Label>
      <Popover open={registerTypeOpen} onOpenChange={setRegisterTypeOpen}>
        <ComboboxTrigger>
          {!currentRegisterType ? (
            <span className={FIELD_PLACEHOLDER_CLASS}>
              {t('search.sidebar.registerTypePlaceholder', {
                defaultValue: 'e.g. HRB',
              })}
            </span>
          ) : (
            <FilterChip onRemove={() => set.registerType('')}>
              <span className="max-w-[220px] truncate">
                {REGISTER_TYPE_OPTIONS.find(
                  (o) => o.value === currentRegisterType,
                )?.label ?? currentRegisterType}
              </span>
            </FilterChip>
          )}
        </ComboboxTrigger>
        <ComboboxContent>
          <Command>
            <CommandList>
              <CommandGroup>
                {registerTypeOptions.map((opt) => (
                  <CommandItem
                    key={opt.value}
                    value={opt.value}
                    onSelect={() => {
                      set.registerType(opt.value);
                      setRegisterTypeOpen(false);
                    }}
                  >
                    <OptionCheck selected={currentRegisterType === opt.value} />
                    <span className="truncate text-xs">{opt.label}</span>
                    <FacetCount
                      count={facetCount('register_type', opt.value)}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </ComboboxContent>
      </Popover>
    </div>
  );
}

/** LEI Code — exact lookup. Independent of the LEI membership filter under
 *  Company profile: this matches a specific 20-char identifier; that one
 *  matches "company has any active LEI". */
export function LeiCodeField({
  t,
  staged,
  set,
  handleFormSubmit,
}: Pick<
  RegisterFilterGroupProps,
  't' | 'staged' | 'set' | 'handleFormSubmit'
>) {
  const { leiCode } = staged;
  return (
    <div className="space-y-1.5">
      <Label className={FIELD_LABEL_CLASS}>
        {t('search.sidebar.leiCode', { defaultValue: 'LEI Code' })}
      </Label>
      <Input
        value={leiCode}
        onChange={(e) => set.leiCode(e.target.value)}
        onKeyDown={submitOnEnter(handleFormSubmit)}
        placeholder={t('search.sidebar.leiCodePlaceholder', {
          defaultValue: 'e.g. 529900T8BM49AURSDO55',
        })}
        maxLength={20}
        className={`h-11 lg:h-9 text-base lg:text-sm font-mono ${INPUT_PLACEHOLDER_CLASS}`}
      />
    </div>
  );
}

/** USt-IdNr — German VAT number. Fixed `DE` prefix sits inside the field as
 *  a decorative label; the user only types digits. Paste of "DE123456789"
 *  naturally drops the DE since the regex strips every non-digit. Backend
 *  gets `DE${digits}` from SearchPageClient. */
export function UstIdnrField({
  t,
  lockOf,
  staged,
  set,
  handleFormSubmit,
}: Pick<
  RegisterFilterGroupProps,
  't' | 'lockOf' | 'staged' | 'set' | 'handleFormSubmit'
>) {
  const { ustIdnr } = staged;
  return (
    <div className="space-y-1.5">
      <FilterLabel lock={lockOf('ustIdnr')} className={FIELD_LABEL_CLASS}>
        {t('search.sidebar.ustIdnr', { defaultValue: 'USt-IdNr.' })}
      </FilterLabel>
      <div className="relative">
        <span
          aria-hidden
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base lg:text-sm font-mono text-muted-foreground select-none"
        >
          DE
        </span>
        <Input
          type="text"
          inputMode="numeric"
          pattern="\d*"
          value={ustIdnr}
          disabled={!!lockOf('ustIdnr')}
          onChange={(e) => set.ustIdnr(e.target.value.replace(/\D/g, ''))}
          onKeyDown={submitOnEnter(handleFormSubmit)}
          placeholder={t('search.sidebar.ustIdnrPlaceholder', {
            defaultValue: '123456789',
          })}
          className={`h-11 lg:h-9 text-base lg:text-sm font-mono pl-9 ${INPUT_PLACEHOLDER_CLASS}`}
        />
      </div>
    </div>
  );
}
